import { plural } from "@amber/contracts";
import { AppLink, PopoverHeading, cx, formatBytes, formatCount } from "@amber/ui";
import type { BudgetLine, HomeResponse } from "@amber/application/contracts";
import { usd } from "./format";
import { Count, Panel, Pop } from "./parts";

type Sustainability = HomeResponse["sustainability"];

function SpendBar({ line }: { line: BudgetLine }) {
  const scale = Math.max(line.allowance, line.forecast, 1) * 1.08;
  const pct = (v: number) => `${String(Math.min(100, (v / scale) * 100))}%`;
  return (
    <div
      role="img"
      aria-label={`${line.name}: ${usd(line.incurred)} incurred, ${usd(line.committed)} committed, forecast ${usd(line.forecast)} of ${usd(line.allowance)}`}
      className="relative h-2 w-full rounded-full bg-surface-2"
    >
      <div className="absolute inset-y-0 left-0 rounded-full bg-link" style={{ width: pct(line.incurred) }} />
      <div className="absolute inset-y-0 bg-link/40" style={{ left: pct(line.incurred), width: pct(line.committed) }} />
      <div className="absolute -inset-y-0.5 w-px bg-fg" style={{ left: pct(line.forecast) }} />
      <div className="absolute -inset-y-1 w-0.5 bg-fg" style={{ left: pct(line.allowance) }} />
      <div className="absolute inset-y-0 w-px bg-muted" style={{ left: pct(line.allowance * line.thresholds.alert) }} />
      <div
        className="absolute inset-y-0 w-px bg-muted"
        style={{ left: pct(line.allowance * line.thresholds.optional) }}
      />
    </div>
  );
}

function SpendCategories({ s, team }: { s: Sustainability; team: boolean }) {
  return (
    <ul role="list" className="m-0 mt-2 list-none space-y-1.5 p-0">
      {s.categories.map((c) => (
        <li key={c.key} className="grid grid-cols-[96px_1fr_auto] items-center gap-2 text-md">
          <Pop label={c.name.replace(" incl. storage", "")} team={team}>
            <PopoverHeading>{c.name}</PopoverHeading>
            <p>{c.covers}</p>
            <dl>
              <dt>Allowance</dt>
              <dd>{usd(c.allowance)}</dd>
              <dt>Incurred</dt>
              <dd>{usd(c.incurred)}</dd>
              <dt>Committed</dt>
              <dd>{usd(c.committed)}</dd>
              <dt>Forecast</dt>
              <dd>{usd(c.forecast)}</dd>
              <dt>Alerts</dt>
              <dd>
                {formatCount(c.thresholds.alert * 100)}% task · {formatCount(c.thresholds.optional * 100)}% optional
                work suspended
              </dd>
            </dl>
            {c.over ? <p className="mt-2 text-waiting">The month-end forecast is over this allowance.</p> : null}
          </Pop>
          <SpendBar line={c} />
          <span className={cx("font-mono whitespace-nowrap", c.over && "text-waiting")}>
            {formatCount(c.forecast)} / {formatCount(c.allowance)}
            {c.over ? " · over" : ""}
          </span>
          {c.state ? <span className="col-span-3 text-sm text-waiting">{c.state}</span> : null}
        </li>
      ))}
    </ul>
  );
}

function HumanWork({ work, team }: { work: Sustainability["human_work"]; team: boolean }) {
  return (
    <>
      <dt>
        <Pop label="Human work" team={team}>
          <PopoverHeading>Human work this week</PopoverHeading>
          <p>Measured from actions in the app, never from time tracking.</p>
          {work.waiting.by_type.map((t) => (
            <p key={t.type} className="text-md">
              <b className="font-strong">{t.label}</b> · {formatCount(t.count)} · {t.needs} · oldest{" "}
              {plural(t.oldest_days, "day", { format: formatCount })}
            </p>
          ))}
          {work.waiting.other_roles > 0 ? (
            <p className="text-muted">
              {plural(work.waiting.other_roles, "more task", { format: formatCount })} open for other roles.
            </p>
          ) : null}
          <AppLink href={work.waiting.href} className="text-link">
            Open tasks →
          </AppLink>
        </Pop>
      </dt>
      <dd className="m-0 text-right">
        <b className="font-strong">{formatCount(work.corrections)}</b> corrections ·{" "}
        <b className="font-strong">{formatCount(work.source_decisions)}</b> source decisions ·{" "}
        <b className="font-strong">{formatCount(work.plan_changes)}</b> plan changes ·{" "}
        <b className="font-strong">{formatCount(work.other_decisions)}</b> other
        <div className={work.waiting.count > 0 ? "text-waiting" : "text-muted"}>
          <Count href={work.waiting.href} count={work.waiting.count} team={team} label="Waiting for a person" inline />{" "}
          waiting for a person
          {work.waiting.oldest_days !== null
            ? ` · oldest ${plural(work.waiting.oldest_days, "day", { format: formatCount })}`
            : ""}
        </div>
      </dd>
    </>
  );
}

function SpendFigures({ s, team }: { s: Sustainability; team: boolean }) {
  return (
    <dl className="m-0 mt-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 border-t border-divider pt-2.5 text-md">
      <dt>
        <Pop label="Cost per eligible" team={team}>
          <PopoverHeading>Cost per eligible communication</PopoverHeading>
          <p>
            Month-to-date spend including committed work, divided by the communications first made eligible since the
            start of the month. Staff time is excluded. Shared, failed and backfill costs count in the month they occur.
            Volatile early in the month.
          </p>
        </Pop>
      </dt>
      <dd className="m-0 text-right">
        {s.cost_per_eligible.this_month === null ? (
          <span className="text-muted">No eligible communications yet this month</span>
        ) : (
          <b className="font-mono font-semibold">≈ {usd(s.cost_per_eligible.this_month, true)}</b>
        )}
        {s.cost_per_eligible.previous_month === null ? null : (
          <span className="text-muted"> · last month {usd(s.cost_per_eligible.previous_month, true)}</span>
        )}
      </dd>
      <dt>
        <Pop label="Archive" team={team}>
          <PopoverHeading>Archive</PopoverHeading>
          <p>
            Stored originals and derived files; shared files count once. Restricted and deleted records still count
            here. Billed under Infrastructure.
          </p>
          {s.archive.largest_platform ? <p>Largest platform: {s.archive.largest_platform}.</p> : null}
          <AppLink href={s.archive.href} className="text-link">
            Archive size per platform →
          </AppLink>
        </Pop>
      </dt>
      <dd className="m-0 text-right">
        <b className="font-mono font-semibold">{formatBytes(s.archive.size_bytes)}</b>
        <span className="text-muted">
          {" "}
          +{formatBytes(s.archive.growth_bytes_per_month)} a month
          {s.archive.deleted_contributing > 0 ? ` · incl. ${formatCount(s.archive.deleted_contributing)} deleted` : ""}
        </span>
      </dd>
      <HumanWork work={s.human_work} team={team} />
    </dl>
  );
}

export function SustainabilityPanel({ data, team }: { data: HomeResponse; team: boolean }) {
  const s = data.sustainability;
  const total = s.total;
  return (
    <Panel
      labelledBy="home-sustainability"
      title="Sustainability"
      lead="Spend this month"
      link={team ? null : { href: "/settings/budgets#spend", label: "Budget" }}
    >
      {total ? (
        <>
          <p className="m-0">
            <span className={cx("font-mono font-semibold", team ? "text-[28px]" : "text-2xl")}>
              {usd(total.forecast)}
            </span>{" "}
            <span className="text-muted">
              forecast of {usd(total.allowance)} · {formatCount(total.percent)}%
            </span>
          </p>
          <p className="m-0 text-md text-muted">
            {usd(total.incurred)} incurred · {usd(total.committed)} committed · {s.month.label}, day {s.month.day} of{" "}
            {s.month.days}
          </p>
          {total.state ? <p className="m-0 text-md text-waiting">{total.state}</p> : null}
        </>
      ) : (
        <p className="m-0 text-muted">No allowances configured.</p>
      )}
      <SpendCategories s={s} team={team} />
      <p className="m-0 mt-1 text-sm text-muted">
        Bars: incurred, committed, forecast and allowance, with marks at the alert thresholds.
      </p>
      <SpendFigures s={s} team={team} />
    </Panel>
  );
}
